import { create } from "zustand";
import {
    ProjectInstrumentAssignmentMap,
    ProjectSourceAssingmentMap,
    ProjectTracksMap,
    ProjectUIState
} from "../model/project";
import {
    ComputedContextFrame,
    ContextPlane,
    NoteDuration
} from "../model/context";
import { AbstractHorizontalContainer } from "../model/containers";
import {
    RydBookmark,
    RydBookmarkSelection
} from "../model/bookmark";
import { LocalFSAdapter } from "../persistence/LocalFSAdapter";


export type StateClassWrapper<T> = {
    stateObject: T
}

export type WorkspaceStore = {
    updates: number;
    forceUpdate: () => void;
    workspaceTracks: ProjectTracksMap;
    setWorkspaceTracks: (tracks: ProjectTracksMap) => void;
    contextPlane: StateClassWrapper<ContextPlane>;
    setContextPlane: (plane: StateClassWrapper<ContextPlane>) => void;
    computedContextFrame: ComputedContextFrame;
    setComputedContextFrame: (frame: ComputedContextFrame) => void;
    lines: AbstractHorizontalContainer<any>[];
    setLines: (lines: AbstractHorizontalContainer<any>[]) => void;
    uiState: ProjectUIState;
    setUIState: (state: ProjectUIState) => void;
    instrumentAssignments: ProjectInstrumentAssignmentMap;
    setInstrumentAssignments: (assignments: ProjectInstrumentAssignmentMap) => void;
    inputSourceAssignments: ProjectSourceAssingmentMap;
    setInputSourceAssignments: (assignments: ProjectSourceAssingmentMap) => void;
    bookmarks: (RydBookmark & RydBookmarkSelection)[];
    setBookmarks: (bookmarks: (RydBookmark & Partial<RydBookmarkSelection>)[]) => void;
    fsAdapter?: LocalFSAdapter;
    setFSAfapter: (path?: LocalFSAdapter) => void;
}

export const workspaceStore = create<WorkspaceStore>((set) => ({
    updates: 0,
    forceUpdate: () => set((state) => ({ updates: state.updates + 1 })),
    workspaceTracks: new Map(),
    setWorkspaceTracks: (tracks: ProjectTracksMap) => set({ workspaceTracks: tracks }),
    contextPlane: { stateObject: undefined as unknown as ContextPlane }, // FIXME: initialize properly
    setContextPlane: (plane: StateClassWrapper<ContextPlane>) => set({ contextPlane: { ...plane } }),
    computedContextFrame: new ComputedContextFrame(),
    setComputedContextFrame: (frame: ComputedContextFrame) => set({ computedContextFrame: frame }),
    lines: [],
    setLines: (lines: AbstractHorizontalContainer<any>[]) => set({ lines }),
    uiState: {
        tracks: new Map(),
        zoomFactor: 480,
        gridResolution: 'bar',
        snapToGrid: true,
        newNoteDuration: NoteDuration.fromBackendValue('QUARTER'),

        editMode: 'SELECT',
        globalKeybindingsSuspended: false,
    },
    setUIState: (state: ProjectUIState) => set({ uiState: state }),
    instrumentAssignments: new Map(),
    setInstrumentAssignments: (assignments: ProjectInstrumentAssignmentMap) => set({ instrumentAssignments: assignments }),
    inputSourceAssignments: new Map(),
    setInputSourceAssignments: (assignments: ProjectSourceAssingmentMap) => set({ inputSourceAssignments: assignments }),
    bookmarks: [],
    setBookmarks: (bookmarks: (RydBookmark & RydBookmarkSelection)[]) => set({ bookmarks }),
    fsAdapter: undefined,
    setFSAfapter: (path?: LocalFSAdapter) => set({ fsAdapter: path }),
}));

export const useWorkspaceStore = () => {
    return workspaceStore((state) => ({
        updates: state.updates,
        forceUpdate: state.forceUpdate,
        workspaceTracks: state.workspaceTracks,
        setWorkspaceTracks: state.setWorkspaceTracks,
        contextFrame: state.contextPlane,
        setContextFrame: state.setContextPlane,
        computedContextFrame: state.computedContextFrame,
        setComputedContextFrame: state.setComputedContextFrame,
        lines: state.lines,
        setLines: state.setLines,
        uiState: state.uiState,
        setUIState: state.setUIState,
        instrumentAssignments: state.instrumentAssignments,
        setInstrumentAssignments: state.setInstrumentAssignments,
        inputSourceAssignments: state.inputSourceAssignments,
        setInputSourceAssignments: state.setInputSourceAssignments,
        localSampleLibraryHandle: state.fsAdapter,
        setLocalSampleLibraryHandle: state.setFSAfapter,
        bookmarks: state.bookmarks,
        setBookmarks: state.setBookmarks,
    }));
}

export const useWorkspaceTracks = () => {
    return workspaceStore((state) => state.workspaceTracks);
}

export const useSetWorkspaceTracks = () => {
    return workspaceStore((state) => state.setWorkspaceTracks);
}
export const useWorkspaceContextPlane = () => {
    return workspaceStore((state) => state.contextPlane);
}

export const useSetWorkspaceContextPlane = () => {
    return workspaceStore((state) => state.setContextPlane);
}

export const useWorkspaceComputedContextFrame = () => {
    return workspaceStore((state) => state.computedContextFrame);
}

export const useSetWorkspaceComputedContextFrame = () => {
    return workspaceStore((state) => state.setComputedContextFrame);
}

export const useWorkspaceLines = () => {
    return workspaceStore((state) => state.lines);
}

export const useSetWorkspaceLines = () => {
    return workspaceStore((state) => state.setLines);
}

export const useWorkspaceUIState = () => {
    return workspaceStore((state) => state.uiState);
}

export const useSetWorkspaceUIState = () => {
    return workspaceStore((state) => state.setUIState);
}

export const useWorkspaceInstrumentAssignments = () => {
    return workspaceStore((state) => state.instrumentAssignments);
}

export const useSetWorkspaceInstrumentAssignments = () => {
    return workspaceStore((state) => state.setInstrumentAssignments);
}

export const useWorkspaceInputSourceAssignments = () => {
    return workspaceStore((state) => state.inputSourceAssignments);
}

export const useSetWorkspaceInputSourceAssignments = () => {
    return workspaceStore((state) => state.setInputSourceAssignments);
}

export const useWorkspaceBookmarks = () => {
    return workspaceStore((state) => state.bookmarks);
}

export const useSetWorkspaceBookmarks = () => {
    return workspaceStore((state) => state.setBookmarks);
}

export const useFSAdapter = () => {
    return workspaceStore((state) => state.fsAdapter);
}

export const useSetFSAdapter = () => {
    return workspaceStore((state) => state.setFSAfapter);
}